/**
 * Listens on the per-session AppSync channel (desktop side of mobile handoff).
 */

const RETRY_DELAY_MS = 3000;

// All message types defined by the AppSync Events WebSocket protocol.
const MessageType = Object.freeze({
    // Server → client: connection lifecycle
    CONNECTION_ACK:       'connection_ack',
    CONNECTION_ERROR:     'connection_error',
    KEEP_ALIVE:           'ka',

    // Server → client: subscription lifecycle
    SUBSCRIBE_SUCCESS:    'subscribe_success',
    SUBSCRIBE_ERROR:      'subscribe_error',
    UNSUBSCRIBE_SUCCESS:  'unsubscribe_success',
    UNSUBSCRIBE_ERROR:    'unsubscribe_error',

    // Server → client: data
    DATA:                 'data',
    BROADCAST_ERROR:      'broadcast_error',

    // Server → client: errors
    ERROR:                'error',
});

function connect(realtimeEndpoint, channel, authHeaders) {

    function open() {
        try {
            // AppSync Events requires auth headers to be passed as a URL-safe base64-encoded
            // subprotocol in the format `header-<base64url>`.
            
            // Convert standard base64 to base64url encoding. This replaces URL-problematic
            // characters: "-" replaces "+", "_" replaces "/" per
            // https://stackoverflow.com/questions/55389211/string-based-data-encoding-base64-vs-base64url
            // Padding ("=") is also removed. Using a package would be overkill for this
            // simple 3-line conversion.
            const encodedHeader = btoa(JSON.stringify(authHeaders))
                .replace(/\+/g, '-')
                .replace(/\//g, '_')
                .replace(/=/g, '');

            const ws = new WebSocket(realtimeEndpoint, ['aws-appsync-event-ws', 'header-' + encodedHeader]);

            let connectionTimeoutMs = null;
            let lastKaAt = null;
            let kaTimer = null;

            function startKaWatchdog() {
                // Polls every second. A missed ka is detected within connectionTimeoutMs + 1000ms
                // in the worst case, depending on when the last ka arrived within the interval.
                kaTimer = setInterval(function () {
                    if (connectionTimeoutMs === null || lastKaAt === null) return;
                    if (Date.now() - lastKaAt > connectionTimeoutMs) {
                        clearInterval(kaTimer);
                        ws.close();
                    }
                }, 1000);
            }

            function stopKaWatchdog() {
                clearInterval(kaTimer);
                kaTimer = null;
            }

            ws.onopen = function () {
                ws.send(JSON.stringify({ type: 'connection_init' }));
            };

            ws.onmessage = function (evt) {
                let msg;
                try {
                    msg = JSON.parse(evt.data);
                } catch (e) {
                    ws.close();
                    return;
                }

                switch (msg.type) {
                    case MessageType.CONNECTION_ACK:
                        // AppSync sends connectionTimeoutMs in the connection_ack payload.
                        connectionTimeoutMs = msg.connectionTimeoutMs || null;
                        lastKaAt = Date.now();
                        startKaWatchdog();
                        ws.send(JSON.stringify({
                            id: 'sub1',
                            type: 'subscribe',
                            channel: channel,
                            authorization: authHeaders
                        }));
                        break;

                    case MessageType.KEEP_ALIVE:
                        lastKaAt = Date.now();
                        break;

                    case MessageType.DATA:
                        let payload;
                        try {
                            payload = JSON.parse(msg.event || '{}');
                        } catch (e) {
                            ws.close();
                            return;
                        }
                        if (payload.handoffComplete) {
                            fetch('/signature/check-the-image', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                                body: 'decrypt-signature=true',
                                redirect: 'follow'
                            }).then(function (response) {
                                window.location.href = response.url;
                            });
                        }
                        break;

                    case MessageType.SUBSCRIBE_SUCCESS:
                        // Subscription confirmed — no action needed.
                        break;

                    case MessageType.SUBSCRIBE_ERROR:
                    case MessageType.UNSUBSCRIBE_ERROR:
                    case MessageType.BROADCAST_ERROR:
                    case MessageType.CONNECTION_ERROR:
                    case MessageType.ERROR:
                        // Any server-side error closes the connection and triggers backoff retry.
                        ws.close();
                        break;

                    case MessageType.UNSUBSCRIBE_SUCCESS:
                        // We don't send unsubscribe messages, but handle gracefully if received.
                        break;

                    default:
                        // Unknown message type — close and retry rather than silently ignore.
                        ws.close();
                        break;
                }
            };

            ws.onclose = function (evt) {
                stopKaWatchdog();

                // 1000 = normal closure (e.g. user navigated away) — don't retry.
                if (evt.code === 1000) return;

                setTimeout(open, RETRY_DELAY_MS);
            };

        } catch (err) {
            setTimeout(open, RETRY_DELAY_MS);
        }
    }

    open();
}

exports.connectAppSyncHandoff = function (realtimeEndpoint, channel, authHeaders) {
    if (realtimeEndpoint && channel && authHeaders) {
        connect(realtimeEndpoint, channel, authHeaders);
    } else {
        // Leaving the throw here, but the environment variables are checked in scan-qr-code
        throw new Error('Missing required parameters for AppSync mobile handoff connection');
    }
};